import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { legalTranslations } from '../translations/legal';

const LegalContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.1rem 2rem 6rem;
  font-family: 'Montserrat', sans-serif;
`;

const LegalHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const LegalTitle = styled.h1`
  font-size: 3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
`;

const ContentSection = styled.section`
  margin-bottom: 3rem;
  max-width: 800px;
  margin: 0 auto 3rem;
`;

const ContentTitle = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #333;
`;

const ContentSubtitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin: 2rem 0 1rem;
  color: #333;
`;

const ContentText = styled.div`
  font-size: 1.1rem;
  color: #666;
  line-height: 1.8;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
  }
  
  li {
    margin-bottom: 0.8rem;
  }
`;

const ImprintSection = styled.div`
  margin-bottom: 3rem;
`;

const ImprintItem = styled.div`
  margin-bottom: 2rem;
`;

const ImprintTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  color: #333;
`;

const ImprintText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #666;
`;

const Legal = () => {
  const { language } = useLanguage();
  const t = legalTranslations[language];
  
  return (
    <LegalContainer>
      <LegalHeader id="impressum">
        <LegalTitle>{t.title}</LegalTitle>
      </LegalHeader>
      
      <ContentSection>
        <ImprintSection>
          <ImprintItem>
            <ImprintTitle>{t.imprint.companyInfo}</ImprintTitle>
            <ImprintText>
              Plix AI GmbH<br />
              Kolonnenstraße 8<br />
              10827 Berlin, Germany
            </ImprintText>
          </ImprintItem>
          
          <ImprintItem>
            <ImprintTitle>{t.imprint.representatives}</ImprintTitle>
            <ImprintText>
              {/* Lucas Pohling<br /> */}
              Fiarra Pohling
            </ImprintText>
          </ImprintItem>
          
          <ImprintItem>
            <ImprintTitle>{t.imprint.contact}</ImprintTitle>
            <ImprintText>info@plix-ai.com</ImprintText>
          </ImprintItem>
          
          <ImprintItem>
            <ImprintTitle>{t.imprint.court}</ImprintTitle>
            <ImprintText>Amtsgericht Berlin-Charlottenburg</ImprintText>
          </ImprintItem>
          
          <ImprintItem>
            <ImprintTitle>{t.imprint.register}</ImprintTitle>
            <ImprintText>HRB 267262 B</ImprintText>
          </ImprintItem>
          
          <ImprintItem>
            <ImprintTitle>{t.imprint.vatId}</ImprintTitle>
            <ImprintText>DE450289227</ImprintText>
          </ImprintItem>
          
          <ImprintItem>
            <ImprintTitle>{t.imprint.responsible}</ImprintTitle>
            <ImprintText>
              Plix AI GmbH<br />
              Kolonnenstraße 8<br />
              10827 Berlin, Germany
            </ImprintText>
          </ImprintItem>
        </ImprintSection>
      </ContentSection>
      
      <ContentSection>
        <ContentTitle>{t.disclaimer.title}</ContentTitle>
        
        <ContentSubtitle>{t.disclaimer.contentLiability}</ContentSubtitle>
        <ContentText>
          <p>
            {t.disclaimer.contentText}
          </p>
        </ContentText>
        
        <ContentSubtitle>{t.disclaimer.linkLiability}</ContentSubtitle>
        <ContentText>
          <p>
            {t.disclaimer.linkText}
          </p>
        </ContentText>
        
        <ContentSubtitle>{t.disclaimer.copyright}</ContentSubtitle>
        <ContentText>
          <p>
            {t.disclaimer.copyrightText}
          </p>
        </ContentText>
        
        <ContentSubtitle>{t.disclaimer.privacy}</ContentSubtitle>
        <ContentText>
          <p>
            {t.disclaimer.privacyText}
          </p>
        </ContentText>
      </ContentSection>
      
      <ContentSection>
        <ContentTitle id="privacy">{t.privacyPolicy.title}</ContentTitle>
        
        <ContentSubtitle>{t.privacyPolicy.generalInfo}</ContentSubtitle>
        <ContentText>
          <p>
            {t.privacyPolicy.generalText}
          </p>
        </ContentText>
        
        <ContentSubtitle>{t.privacyPolicy.dataCollection}</ContentSubtitle>
        <ContentText>
          <p>
            {t.privacyPolicy.dataCollectionText}
          </p>
        </ContentText>
        
        <ContentSubtitle>{t.privacyPolicy.cookies}</ContentSubtitle>
        <ContentText>
          <p>
            {t.privacyPolicy.cookiesText}
          </p>
        </ContentText>
        
        <ContentSubtitle>{t.privacyPolicy.hosting}</ContentSubtitle>
        <ContentText>
          <p>
            {t.privacyPolicy.hostingText} {language === 'de' ? (
              <>Weitere Informationen finden Sie in der <a href="https://www.cloudflare.com/de-de/privacypolicy/" target="_blank" rel="noopener noreferrer">Datenschutzerklärung von Cloudflare</a>.</>
            ) : (
              <>For more information, please see <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare's Privacy Policy</a>.</>
            )}
          </p>
        </ContentText>
        
        <ContentSubtitle>{t.privacyPolicy.rights}</ContentSubtitle>
        <ContentText>
          <p>
            {t.privacyPolicy.rightsText}
          </p>
        </ContentText>
        
        <ContentSubtitle>{t.privacyPolicy.contactTitle}</ContentSubtitle>
        <ContentText>
          <p>
            {t.privacyPolicy.contactText}<br />
            Plix AI GmbH<br />
            Kolonnenstraße 8<br />
            10827 Berlin, Germany<br />
            {language === 'de' ? 'E-Mail: ' : 'Email: '}info@plix-ai.com
          </p>
        </ContentText>
      </ContentSection>
    </LegalContainer>
  );
};

export default Legal; 